import { get } from 'lodash';
import React from 'react';
import SummaryLine from 'src/components/ui-component/form-component/abschluss-page/SummaryLine';
import { useApplication } from 'src/context/ApplicationContext';
import { DisplayDate, DisplayDateTime, DisplayDecimal, DisplayEuro } from 'src/utils/formats';

// import schemas
import { Typography } from '@mui/material';
import { ANTRAG_VERBUNDPARTNER } from 'src/lib/constants/componentsPath';
import { ANTRAGBERECHTIGTE_TYP, ANTRAGS_FORMULAREN, VORHABENS_ART } from 'src/lib/constants/constant';
import Schema from 'src/lib/paths/antrag_FULL_Paths.json';
import { getErrorMessage, getHasError } from 'src/lib/validation/validationHelperFunction';
import { mapIndexOnlybyPath } from 'src/utils/formStruc';
import { enumT, isTrue } from 'src/utils/helper';
import { getAntragstellerName } from '../application-helper-function/AppHelperfunctions';
const P = Schema.attributes;

const SummaryTitel = () => {
	return <Typography variant='h6'>Projektdaten</Typography>;
};

const SummaryDatumDerEinreichung = () => {
	const { application } = useApplication();

	return (
		<SummaryLine
			label='Datum der Einreichung:'
			value={
				get(application, P.Allgemein_Eingereicht_Am.path) ?
					DisplayDateTime(get(application, P.Allgemein_Eingereicht_Am.path))
				:	'noch nicht eingereicht'
			}
		/>
	);
};

const SummaryProjektTitel = () => {
	const { application } = useApplication();

	return <SummaryLine label='Projekttitel:' value={get(application, P.Allgemein_Projekttitel.path)} />;
};

const SummaryNameDerOrganisation = ({ label }: { label?: string }) => {
	const { application } = useApplication();
	const antragstellerName = getAntragstellerName(application);
	return <SummaryLine boldLabel showBackground label={label || 'Antragstellende Organisation:'} value={antragstellerName} />;
};
const SummaryOrganisationAntragberechtigtAls = () => {
	const { application } = useApplication();

	return (
		<SummaryLine
			label='Antragsberechtigt als:'
			value={enumT(get(application, P.Organisation_Antragsberechtigt_Als.schema.attributes.Allgemein_Bezeichnung.path, ''))}
		/>
	);
};

const SummaryOrganisationFoerderQuote = () => {
	const { application, errors, tree } = useApplication();

	if (get(application, P.Allgemein_Vorhabensart.path) === VORHABENS_ART.Verbundvorhaben && get(application, P.Verbundpartner.path, []).length > 0) {
		return (
			<SummaryLine
				label='Förderquote:'
				value={DisplayDecimal(get(application, P.Orga_Foerderquote_Gesamt.path, 0), true)}
				error={getHasError(errors, tree, P.Orga_Foerderquote_Gesamt)}
				errorMessage={getErrorMessage(errors, P.Orga_Foerderquote_Gesamt.path)}
			/>
		);
	}

	return <></>;
};

const SummaryVerbundpartnerDetails = () => {
	const { application, errors, tree } = useApplication();

	return (
		<>
			{get(application, P.Verbundpartner.path, []).map((eachPartner: any, index: number) => {
				const objWithIndex = mapIndexOnlybyPath(index, P.Verbundpartner, P.Verbundpartner.schema.attributes.Foerderquote_Gesamt_403741);

				const isAntragsberechtigtAlsHochschule =
					get(eachPartner, `${ANTRAG_VERBUNDPARTNER.Antragberechtigt}.attributes.Allgemein_Typ`) === ANTRAGBERECHTIGTE_TYP.Forschungseinrichtung;
				const isNeueHochschule = isTrue(get(eachPartner, 'attributes.Neue_Hochschule')) || isTrue(get(eachPartner, 'Neue_Hochschule'));

				const name =
					isAntragsberechtigtAlsHochschule ?
						isNeueHochschule ? get(eachPartner, 'Rechtsverbindlicher_Name_Der_Organisation')
						:	get(eachPartner, `${ANTRAG_VERBUNDPARTNER.Hochschule}.attributes.Geschaeftsbezogene_Daten_Name_Der_Organisation`)
					:	get(eachPartner, 'Rechtsverbindlicher_Name_Der_Organisation');

				return (
					<React.Fragment key={index}>
						<SummaryLine boldLabel showBackground label={`Verbundpartner ${index + 1}:`} value={name} />
						<SummaryLine
							label={`Antragsberechtigt als:`}
							value={get(eachPartner, `${ANTRAG_VERBUNDPARTNER.Antragberechtigt}.attributes.Allgemein_Bezeichnung`)}
						/>
						<SummaryLine
							label={`Förderquote:`}
							value={DisplayDecimal(get(eachPartner, 'Foerderquote_Gesamt_403741', 0), true)}
							error={getHasError(errors, tree, objWithIndex)}
							errorMessage={getErrorMessage(errors, objWithIndex.path)}
						/>
					</React.Fragment>
				);
			})}
		</>
	);
};

const SummaryGeplanteProjektLaufzeit = () => {
	const { application } = useApplication();

	if (!isTrue(get(application, P.Allgemein_Vorzeitiger_Vorhabenbeginn_Freigeschaltet.path))) {
		return (
			<SummaryLine
				label='Geplante Projektlaufzeit:'
				value={`${DisplayDate(get(application, P.Allgemein_Geplante_Projektlaufzeit_von.path))} bis ${DisplayDate(get(application, P.Allgemein_Geplante_Projektlaufzeit_bis.path))}`}
			/>
		);
	}

	return <></>;
};

const SummaryVorzeitigerVorhabenBeantragt = () => {
	const { application } = useApplication();

	if (isTrue(get(application, P.Allgemein_Vorzeitiger_Vorhabenbeginn_Freigeschaltet.path))) {
		return (
			<>
				<SummaryLine label='Vorzeitiger Vorhabenbeginn:' value='beantragt' />
			</>
		);
	}
	return <></>;
};

const SummaryVorzeitigerVorhabenLaufzeit = () => {
	const { application } = useApplication();

	if (isTrue(get(application, P.Allgemein_Vorzeitiger_Vorhabenbeginn_Freigeschaltet.path))) {
		return (
			<>
				<SummaryLine
					label='Geplante Projektlaufzeit bei vorzeitigem Vorhabenbeginn:'
					value={`${DisplayDate(get(application, P.Vorzeitiger_Vorhabenbeginn.schema.attributes.Vorzeitiger_Vorhabenbeginn_Ab.path))} bis ${DisplayDate(get(application, P.Allgemein_Geplante_Projektlaufzeit_bis.path))}`}
				/>
			</>
		);
	}
	return <></>;
};

const SummaryGeplanteProjektAusgabe = () => {
	const { application } = useApplication();
	return (
		<SummaryLine
			label='Geplante Projektausgaben:'
			showBackground
			boldLabel
			value={DisplayEuro(get(application, P.Allgemein_Geplante_Projektausgaben.path, 0))}
		/>
	);
};

const SummaryBeantragteFoerderung = () => {
	const { application, errors, tree } = useApplication();
	return (
		<SummaryLine
			label='Beantragte Förderung:'
			showBackground
			boldLabel
			value={DisplayEuro(get(application, P.Allgemein_Beantragte_Zuwendung.path, 0))}
			error={getHasError(errors, tree, P.Allgemein_Beantragte_Zuwendung)}
			errorMessage={getErrorMessage(errors, P.Allgemein_Beantragte_Zuwendung.path)}
		/>
	);
};

const SummaryBeantragteFoerderquote = () => {
	const { application } = useApplication();
	const antragsFormular = get(application, P.Allgemein_Antragsformular.path, '');
	if (antragsFormular === ANTRAGS_FORMULAREN.StartUpStipendium) {
		return <></>;
	}
	return (
		<SummaryLine
			label='Beantragte Förderquote:'
			showBackground
			boldLabel
			value={DisplayDecimal(get(application, P.Bewilligung_Foerderquote_Gesamt.path, 0), true)}
		/>
	);
};

export {
	SummaryBeantragteFoerderquote,
	SummaryBeantragteFoerderung,
	SummaryDatumDerEinreichung,
	SummaryGeplanteProjektAusgabe,
	SummaryGeplanteProjektLaufzeit,
	SummaryNameDerOrganisation,
	SummaryOrganisationAntragberechtigtAls,
	SummaryOrganisationFoerderQuote,
	SummaryProjektTitel,
	SummaryTitel,
	SummaryVerbundpartnerDetails,
	SummaryVorzeitigerVorhabenBeantragt,
	SummaryVorzeitigerVorhabenLaufzeit,
};
